import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  StyleSheet,
  Text,
  TouchableOpacity,
  SafeAreaView,
  Platform,
  Dimensions,
} from 'react-native';
import { MapView, MapMarker } from '../components/Map';
import * as Location from 'expo-location';
import { useTheme } from '../contexts/ThemeContext';
import { useMapProvider } from '../contexts/MapProviderContext';
import { getColors } from '../styles/theme';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { useToast } from '../contexts/ToastContext';
import { listHotspots } from '../services/database';

const { width, height } = Dimensions.get('window');

const MapPickerScreen = ({ route, navigation }) => {
  const { initialLatitude, initialLongitude, hotspotId } = route.params || {};
  const { isDarkMode } = useTheme();
  const { showStreetLabels } = useMapProvider();
  const colors = getColors(isDarkMode);
  const { showToast } = useToast();
  const mapRef = useRef(null);

  const [selectedCoordinates, setSelectedCoordinates] = useState(
    initialLatitude && initialLongitude
      ? { latitude: initialLatitude, longitude: initialLongitude }
      : null
  );
  // Initialize region with default to show map immediately, then update to user location if available
  const [region, setRegion] = useState(
    initialLatitude && initialLongitude
      ? {
          latitude: initialLatitude,
          longitude: initialLongitude,
          latitudeDelta: 0.01,
          longitudeDelta: 0.01,
        }
      : {
          latitude: 37.78825, // Temporary default - will update to user location
          longitude: -122.4324,
          latitudeDelta: 0.01,
          longitudeDelta: 0.01,
        }
  );
  const [hotspots, setHotspots] = useState([]);
  const [selectedHotspot, setSelectedHotspot] = useState(null);
  const [isMovingHotspot, setIsMovingHotspot] = useState(false);

  useEffect(() => {
    // Region is already initialized in useState, so map should render immediately
    // Try to get user location if no initial coords provided (will update region when received)
    if (!initialLatitude || !initialLongitude) {
      requestLocationPermission();
    }
    
    fetchHotspots();
  }, []);

  const requestLocationPermission = async () => {
    try {
      const { status } = await Location.requestForegroundPermissionsAsync();
      
      if (status === 'granted') {
        const location = await Location.getCurrentPositionAsync({});
        
        // Always update region with user's location if we're not editing an existing hotspot
        // (if initial coords were provided, keep them; otherwise use user's location)
        if (!initialLatitude || !initialLongitude) {
          const newRegion = {
            latitude: location.coords.latitude,
            longitude: location.coords.longitude,
            latitudeDelta: 0.01,
            longitudeDelta: 0.01,
          };
          setRegion(newRegion);
        }
      } else {
        // Permission denied - use default region (already set in useState)
        // No action needed since default is already set
      }
    } catch (error) {
      console.error('Error requesting location:', error);
      // Error getting location - default region is already set in useState
    }
  };

  const fetchHotspots = async () => {
    try {
      const fetchedHotspots = await listHotspots();
      setHotspots(fetchedHotspots);

      // If editing a hotspot, mark it as selected
      if (hotspotId) {
        const hotspot = fetchedHotspots.find((h) => h.id === hotspotId);
        if (hotspot) {
          setSelectedHotspot(hotspot);
          setSelectedCoordinates({
            latitude: hotspot.latitude,
            longitude: hotspot.longitude,
          });
          setRegion({
            latitude: hotspot.latitude,
            longitude: hotspot.longitude,
            latitudeDelta: 0.01,
            longitudeDelta: 0.01,
          });
        }
      }
    } catch (error) {
      console.error('Error fetching hotspots:', error);
    }
  };

  const handleMapPress = (event) => {
    if (event.nativeEvent && event.nativeEvent.coordinate) {
      const { latitude, longitude } = event.nativeEvent.coordinate;
      setSelectedCoordinates({ latitude, longitude });
      setIsMovingHotspot(false);
      setSelectedHotspot(null);
    }
  };

  const handleHotspotPress = (hotspot) => {
    setSelectedHotspot(hotspot);
    setSelectedCoordinates({
      latitude: hotspot.latitude,
      longitude: hotspot.longitude,
    });
    setIsMovingHotspot(true);
  };

  const handleConfirm = () => {
    if (!selectedCoordinates) {
      showToast('Please select a location on the map', 'error');
      return;
    }

    // Return coordinates to the previous screen via navigation params
    navigation.navigate('AddEditHotspot', {
      ...route.params,
      selectedCoordinates: selectedCoordinates,
    });
  };

  const handleCancel = () => {
    navigation.goBack();
  };

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]}>
      {/* Header */}
      <View style={[styles.header, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
        <TouchableOpacity style={styles.backButton} onPress={handleCancel}>
          <Icon name="arrow-back" size={24} color={colors.text} />
        </TouchableOpacity>
        <Text style={[styles.headerTitle, { color: colors.text }]}>
          {isMovingHotspot ? 'Move Hotspot' : 'Pick Location'}
        </Text>
        <View style={{ width: 28 }} />
      </View>

      {/* Instructions */}
      <View style={[styles.instructionsContainer, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
        <Icon name="info-outline" size={20} color={colors.primary} />
        <Text style={[styles.instructionsText, { color: colors.text }]}>
          {isMovingHotspot
            ? `Moving "${selectedHotspot?.name}". Click on the map to set new location.`
            : 'Click on the map to select coordinates, or click an existing hotspot to move it.'}
        </Text>
      </View>

      {/* Map */}
      <View style={styles.mapContainer}>
        <MapView
          ref={mapRef}
          region={region}
          onRegionChange={setRegion}
          style={styles.map}
          onPress={handleMapPress}
          showsUserLocation={true}
          isDarkMode={isDarkMode}
          showStreetLabels={showStreetLabels}
        >
          {/* Show all hotspots */}
          {hotspots.map((hotspot) => (
            <MapMarker
              key={hotspot.id}
              coordinate={{
                latitude: hotspot.latitude,
                longitude: hotspot.longitude,
              }}
              title={hotspot.name}
              isHotspot={true}
              customMarker={hotspot}
              onPress={() => handleHotspotPress(hotspot)}
            />
          ))}

          {/* Show selected coordinates marker */}
          {selectedCoordinates && (
            <MapMarker
              coordinate={selectedCoordinates}
              pinColor="#FF5722"
              title="Selected Location"
            />
          )}
        </MapView>
      </View>

      {/* Selected coordinates display */}
      {selectedCoordinates && (
        <View style={[styles.coordinatesContainer, { backgroundColor: colors.surface, borderTopColor: colors.border }]}>
          <View style={styles.coordinatesRow}>
            <View style={styles.coordinateItem}>
              <Text style={[styles.coordinateLabel, { color: colors.textSecondary }]}>Latitude</Text>
              <Text style={[styles.coordinateValue, { color: colors.text }]}>
                {selectedCoordinates.latitude.toFixed(6)}
              </Text>
            </View>
            <View style={styles.coordinateItem}>
              <Text style={[styles.coordinateLabel, { color: colors.textSecondary }]}>Longitude</Text>
              <Text style={[styles.coordinateValue, { color: colors.text }]}>
                {selectedCoordinates.longitude.toFixed(6)}
              </Text>
            </View>
          </View>
        </View>
      )}

      {/* Action buttons */}
      <View style={[styles.actionContainer, { backgroundColor: colors.surface, borderTopColor: colors.border }]}>
        <TouchableOpacity
          style={[styles.cancelButton, { borderColor: colors.border }]}
          onPress={handleCancel}
        >
          <Text style={[styles.cancelButtonText, { color: colors.text }]}>Cancel</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.confirmButton, { backgroundColor: colors.primary }]}
          onPress={handleConfirm}
          disabled={!selectedCoordinates}
        >
          <Icon name="check" size={20} color="white" />
          <Text style={styles.confirmButtonText}>Confirm Location</Text>
        </TouchableOpacity>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 16,
    borderBottomWidth: 1,
  },
  backButton: {
    padding: 4,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
  },
  instructionsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    gap: 8,
    borderBottomWidth: 1,
  },
  instructionsText: {
    flex: 1,
    fontSize: 14,
    lineHeight: 20,
  },
  mapContainer: {
    flex: 1,
    position: 'relative',
    backgroundColor: '#f5f5f5',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
  },
  map: {
    flex: 1,
  },
  coordinatesContainer: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderTopWidth: 1,
  },
  coordinatesRow: {
    flexDirection: 'row',
    gap: 16,
  },
  coordinateItem: {
    flex: 1,
  },
  coordinateLabel: {
    fontSize: 12,
    marginBottom: 4,
  },
  coordinateValue: {
    fontSize: 16,
    fontWeight: '600',
    fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
  },
  actionContainer: {
    flexDirection: 'row',
    paddingHorizontal: 16,
    paddingVertical: 16,
    gap: 12,
    borderTopWidth: 1,
  },
  cancelButton: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 10,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  confirmButton: {
    flex: 2,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 14,
    borderRadius: 10,
  },
  confirmButtonText: {
    fontSize: 16,
    fontWeight: 'bold',
    color: 'white',
  },
});

export default MapPickerScreen;

